import { api } from "@/lib/api";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

export async function getSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch (_) { return null; }
}

export async function subscribeToPush({ language = "es", memberId = null } = {}) {
  if (!isPushSupported()) throw new Error("Push not supported in this browser.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("permission_denied");

  const { data } = await api.get("/push/public-key");
  if (!data?.public_key) throw new Error("VAPID public key missing");

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    language,
    member_id: memberId,
    user_agent: navigator.userAgent || "",
  });
  return sub;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  try {
    const sub = await getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      try { await sub.unsubscribe(); } catch (_) { /* browser already forgot the sub */ }
      await api.post("/push/unsubscribe", { endpoint });
    }
  } catch (_) { /* best-effort — never surface unsubscribe errors */ }
}

/**
 * Auto-heal subscriptions silently revoked by the OS (iOS Safari revokes
 * a subscription after 3 "silent push" events — where the SW receives
 * a push but does not call showNotification with a valid notification).
 *
 * If the browser reports permission=granted but there is NO active push
 * subscription, we assume iOS revoked it and quietly re-subscribe. This
 * restores delivery without any user action.
 *
 * If there IS an active local subscription we still POST it to the backend
 * (the /push/subscribe endpoint upserts by endpoint, so re-posting is a
 * no-op if the server already knows about it, and it re-inserts the row
 * if the backend previously purged the sub due to a 410 from Apple/Google).
 * This is critical for iOS: the OS can revoke the sub at the *push service*
 * side (leading to 410s server-side) while `pushManager.getSubscription()`
 * still returns the now-dead sub. Without this re-post, the backend row
 * stays deleted and the device never receives another notification.
 *
 * Safe to call on every app launch — it's idempotent by design.
 */
export async function ensureSubscriptionAlive({ language = "es", memberId = null } = {}) {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const perm = await getPermission();
    if (perm !== "granted") return { ok: false, reason: "not_granted" };

    const { data } = await api.get("/push/public-key");
    if (!data?.public_key) return { ok: false, reason: "no_vapid_key" };

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    let action = "already_subscribed";

    if (!sub) {
      // OS revoked at the browser layer — create a fresh subscription.
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      action = "resubscribed";
    }

    // Always re-post to the backend so an OS-side revoke (that already
    // caused the backend to delete the row via a 410 from the push
    // service) is transparently healed on the next app open.
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      language,
      member_id: memberId,
      user_agent: navigator.userAgent || "",
    });
    return { ok: true, reason: action };
  } catch (err) {
    return { ok: false, reason: "error", error: String(err?.message || err) };
  }
}
